import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * The overlays Android's hardware Back must dismiss (Module 28, WP-NAV.10):
 * the shared Settings and Shop popups. Deliberately NOT `.modal-overlay`
 * (ModalSystem) — UX-004 locked those against casual dismissal, and hardware
 * Back is the same gesture class as a backdrop tap.
 */
const DISMISSIBLE_OVERLAY_SELECTOR = '.settings-popup-overlay, .shop-popup-overlay';

export interface HardwareBackDeps {
  hasOpenPopup: () => boolean;
  dismissTopPopup: () => void;
  goBack: () => void;
  exitApp: () => void;
}

/**
 * Pure decision logic, extracted for direct unit testing: an open popup wins
 * (Back = dismiss, exactly like ESC/backdrop); otherwise normal history
 * navigation; at the history root, default Android behaviour (exit).
 */
export function handleHardwareBack(canGoBack: boolean, deps: HardwareBackDeps): void {
  if (deps.hasOpenPopup()) {
    deps.dismissTopPopup();
  } else if (canGoBack) {
    deps.goBack();
  } else {
    deps.exitApp();
  }
}

/**
 * Installs the Android hardware-Back listener (native builds only — a no-op on
 * web, where the browser owns Back). Dismissal is a synthetic Escape keydown:
 * both shared popups already close on ESC via their own document listeners, so
 * whichever popup is actually open handles it — no popup registry, no reaching
 * into per-React-root state (the games' SettingsPopups live in roots this
 * module can't see).
 */
export function installHardwareBackHandler(): void {
  if (!Capacitor.isNativePlatform()) return;
  void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    handleHardwareBack(canGoBack, {
      hasOpenPopup: () => document.querySelector(DISMISSIBLE_OVERLAY_SELECTOR) !== null,
      dismissTopPopup: () =>
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' })),
      goBack: () => window.history.back(),
      exitApp: () => void CapacitorApp.exitApp(),
    });
  });
}
